/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useState } from "react";

import { Group, Namespace, useTranslate } from "localization";
import { SubmissionSlotData } from "types/SubmissionSlots";

import { RequestTaskUpdate } from "API/Endpoints/Tasks/payloads";

import { useAppDispatch } from "utilities/hooks/general";

import { addNotification } from "Common/Snackbar";

interface UseSubmitModal {
  submissionSuccess: boolean;
  loading: boolean;
  handleSubmit: () => Promise<void>;
}

type Props = {
  submitAction: () => Promise<
    RequestTaskUpdate | SubmissionSlotData | null
  > | void;
  onFailure: (e: any) => void;
  onClose: () => void;
};

export const useSubmitModal = (props: Props): UseSubmitModal => {
  const { submitAction, onFailure, onClose } = props;

  const { translate } = useTranslate(Namespace.DEFAULT, Group.SUBMIT_MODAL);

  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [submissionSuccess, setSubmissionSuccess] = useState<boolean>(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const submittedActionResponse: any = await submitAction();

      switch (true) {
        case submittedActionResponse?.status === 400: {
          void dispatch(
            addNotification({
              message: translate("error"),
              variant: "error",
            })
          );
          onFailure(submittedActionResponse);
          break;
        }

        case submittedActionResponse?.type === "custom": {
          const { headerMessage, message } = submittedActionResponse;
          void dispatch(
            addNotification({
              headerMessage,
              message,
              variant: "error",
            })
          );
          onClose();
          break;
        }

        case submittedActionResponse?.status === 403:
          void dispatch(
            addNotification({
              message: translate("permissionDenied", {
                namespace: Namespace.DEFAULT,
              }),
              variant: "error",
            })
          );
          onClose();
          break;

        default:
          setSubmissionSuccess(true);
      }
    } catch (e: any) {
      const errMessage = e?.data?.detail;
      void dispatch(
        addNotification({
          message: errMessage || translate("error"),
          variant: "error",
        })
      );
      onFailure(e);
    }
    setLoading(false);
  };

  return { submissionSuccess, loading, handleSubmit };
};
